/**
 * Appointment events — ECS API server cutover (GET-2682).
 *
 * All ECS emits funnel through an in-memory queue + ``/appointment-events/batch``:
 *
 *   - ``enqueueAppointmentEventViaECS(...)`` — append to queue, microtask flush.
 *     Coalesces rapid bursts (A→B→A→B tab switches, simultaneous edit + view
 *     events) into a single batched POST.
 *
 *   - ``flushAppointmentEventQueueSync()`` — pagehide-time flush. Fires one
 *     keepalive POST for whatever's queued at unload, so multi-tracker
 *     finalize on tab close becomes one request instead of N. The queue
 *     replaces the single-POST path that #6289 introduced.
 *
 * Per-batch emit-attempt breadcrumb. Distinguishes "never attempted"
 * (e.g., a bypassed call site) from "attempted and failed in transport" in
 * Sentry — without this distinction, telemetry can't tell us which class
 * of bug is dominant when we look at missing-event rates in Snowflake.
 *
 *  ECS server derives ``actor_type`` / ``actor_id`` from the JWT, so request
 *  bodies cannot spoof either field.
 */
import { createApiClient } from "~/api/apiClient";
import type { components } from "~/api/generated/api-server-types";
import { errorReporter } from "~/error-handling";
import { stopAllActiveTrackers } from "~/services/time-tracking/trackerRegistry";

type EmitRequest = components["schemas"]["EmitAppointmentEventRequest"];

// Single shared client across the session — apiClient.ts documents that
// the client is safe to cache (it injects a fresh JWT per request via
// the auth middleware). Time-tracking emits are high-frequency, so
// rebuilding the middleware chain per call would be needless overhead.
const apiClient = createApiClient();

// ---------------------------------------------------------------------------
// Batch queue
// ---------------------------------------------------------------------------

interface QueuedEvent {
  event: EmitRequest;
  // ``resolve`` fires once the batch flush attempt completes (success or
  // logged failure). Callers are fire-and-forget today (every call site
  // is ``void lambdaService.emitAppointmentEvent(...)``) so we never
  // reject — that would surface as an unhandled rejection in Sentry and
  // doesn't add observability beyond the per-event warning we already
  // log on failure.
  resolve: () => void;
}

const queue: QueuedEvent[] = [];
let flushScheduled = false;

function scheduleFlush(): void {
  if (flushScheduled) return;
  flushScheduled = true;
  queueMicrotask(() => {
    void flushAppointmentEventQueue();
  });
}

async function flushAppointmentEventQueue(): Promise<void> {
  flushScheduled = false;
  if (queue.length === 0) return;
  const items = queue.splice(0);
  const events = items.map((i) => i.event);

  // Emit-attempt breadcrumb. One per batch, info-level — pairs with the
  // per-event warning on failure to give us "attempted vs succeeded vs
  // never tried" in Sentry. Without this, a code path that silently
  // skips the queue (flag-check race, bypassed call site, etc.) is
  // indistinguishable from a transport drop.
  errorReporter.reportMessage("Appointment event batch attempt", {
    level: "info",
    tags: {
      component: "AppointmentEvents",
      operation: "emit-via-ecs-batch",
    },
    extra: {
      batchSize: events.length,
      actions: events.map((e) => `${String(e.entity)}.${String(e.action)}`),
    },
  });

  try {
    const { data, error } = await apiClient.POST("/appointment-events/batch", {
      body: { events },
    });
    if (error || !data) {
      throw new Error(
        `non-2xx from /appointment-events/batch: ${
          typeof error === "object" ? JSON.stringify(error) : String(error)
        }`,
      );
    }
  } catch (e) {
    // One warning per event so per-action failure rates remain
    // measurable — matches the granularity of the previous single-POST
    // path so we don't lose comparability with the pre-batch baseline.
    for (const item of items) {
      errorReporter.reportMessage("Appointment event emit failed", {
        level: "warning",
        tags: {
          component: "AppointmentEvents",
          operation: "emit-via-ecs-batch",
          entity: String(item.event.entity),
          action: String(item.event.action),
        },
        extra: {
          appointmentId: item.event.appointment_id,
          entityId: item.event.entity_id ?? null,
          batchSize: items.length,
          error: e instanceof Error ? e.message : String(e),
        },
      });
    }
  } finally {
    items.forEach((i) => i.resolve());
  }
}

/**
 * Pagehide-time sync flush. Fire-and-forget keepalive POST — by the time
 * this runs, the page may already be tearing down, so awaiting anything
 * is unsafe. The browser will deliver the request as long as we initiate
 * it before the unload completes.
 *
 * Idempotent on empty queue, so it's safe to wire to multiple unload
 * events (``pagehide`` + ``visibilitychange:hidden``) without
 * double-flushing.
 */
export function flushAppointmentEventQueueSync(): void {
  if (queue.length === 0) return;
  const items = queue.splice(0);
  const events = items.map((i) => i.event);

  // Telemetry parity with the async path: a Sentry breadcrumb before
  // the keepalive POST so a dropped keepalive is distinguishable from
  // "no events were ever queued at unload" in Sentry. Without this,
  // the highest-risk transport (unload) is the one with no observability.
  // Sentry SDK buffers messages and best-effort flushes during unload.
  errorReporter.reportMessage("Appointment event batch attempt", {
    level: "info",
    tags: {
      component: "AppointmentEvents",
      operation: "emit-via-ecs-batch-sync",
    },
    extra: {
      batchSize: events.length,
      actions: events.map((e) => `${String(e.entity)}.${String(e.action)}`),
    },
  });

  // Don't await. Don't wrap in try/catch — there's no error handler that
  // can do anything useful at this point.
  void apiClient.POST("/appointment-events/batch", {
    body: { events },
    keepalive: true,
  });

  items.forEach((i) => i.resolve());
}

// ---------------------------------------------------------------------------
// Public emit API
// ---------------------------------------------------------------------------

export interface EmitAppointmentEventECSInput {
  appointmentId: string;
  // ``entity`` and ``action`` use the OpenAPI-generated union types
  // directly — the pydantic-generated equivalents are identical string
  // unions so callers passing either resolve structurally without a cast.
  entity: EmitRequest["entity"];
  action: EmitRequest["action"];
  entityId?: string | null;
  properties?: Record<string, unknown> | null;
}

/**
 * Enqueue an appointment event for batched emit. Returns when the batch
 * containing this event has been attempted (success or logged failure).
 *
 * Callers are fire-and-forget — every call site is
 * ``void lambdaService.emitAppointmentEvent(...)`` — so the returned
 * promise is mostly informational.
 */
export function enqueueAppointmentEventViaECS(
  input: EmitAppointmentEventECSInput,
): Promise<void> {
  return new Promise<void>((resolve) => {
    queue.push({
      event: {
        appointment_id: input.appointmentId,
        entity: input.entity,
        action: input.action,
        entity_id: input.entityId ?? null,
        // ``undefined`` (not ``{}``) when the caller has no properties —
        // matches the legacy Lambda payload so downstream consumers that
        // distinguish "absent" from "empty object" see the same shape.
        properties: input.properties ?? undefined,
      },
      resolve,
    });
    scheduleFlush();
  });
}

// ---------------------------------------------------------------------------
// App-boot wiring
// ---------------------------------------------------------------------------

let unloadFlushRegistered = false;

/**
 * Register page-lifecycle handlers for the queue. Idempotent — safe to
 * call from multiple init paths.
 *
 * The two events have intentionally different semantics:
 *
 *   - ``pagehide`` = the page is actually being torn down (close,
 *     navigate, refresh). Stop every active tracker — each
 *     ``stopTracking`` synchronously enqueues a finalize event — then
 *     drain in one keepalive batch POST.
 *
 *   - ``visibilitychange:hidden`` = the page is just backgrounded (tab
 *     switch, minimize, screen lock). The user may come back. Don't
 *     finalize trackers here — let the per-tracker visibility handler
 *     pause them so the timer doesn't count background time. We still
 *     drain the queue so any already-enqueued events ship before a
 *     potential mobile-OS kill, but we don't synthesize new emits.
 *
 * Conflating the two — as an earlier revision did — meant every
 * tab-switch produced a TAB_VIEWED with ``outcome: "ABANDONED"`` and
 * ``durationMs`` reset on return. Pause-on-minimize was effectively
 * dead code because the global handler stopped trackers before the
 * per-tracker handler could pause them.
 *
 * Mobile-OS background-kill case: ``saveToLS`` runs from
 * ``pauseTracking``, so state is persisted at the moment of pause.
 * If the OS kills the tab before any ``pagehide`` fires,
 * ``recoverOrphanedSessions`` on the next page load flushes the entry
 * as ``ABANDONED`` with the correct accumulated duration.
 */
export function registerAppointmentEventUnloadFlush(): void {
  if (unloadFlushRegistered) return;
  if (typeof window === "undefined") return;
  unloadFlushRegistered = true;

  window.addEventListener("pagehide", () => {
    stopAllActiveTrackers("ABANDONED");
    flushAppointmentEventQueueSync();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      // Drain only — don't finalize trackers. The per-tracker
      // visibilitychange handler pauses each active tracker (which
      // saveToLS-es), so any enqueued events from prior emit paths get
      // shipped, but no new TAB_VIEWED is synthesized.
      flushAppointmentEventQueueSync();
    }
  });
}
